'use client';

import Link from 'next/link';
import { useRecentSearches } from '../_hooks/useRecentSearches';
import {
  PROXY_PREFIX,
  bumpPastDates,
  buildQueryString,
  parseOccupancyCMS,
  shortDestLabel,
  summarizeOccupancy,
} from '../config';

// JSX/classes ported verbatim from bip_website_designs/src/components/hotels/RecentSearches.jsx.
// Only dynamic data (city, dates, guests, href) is bound.

function fmtRangeDisplay(checkin: string, checkout: string): string {
  const ci = new Date(checkin + 'T00:00:00');
  const co = new Date(checkout + 'T00:00:00');
  if (isNaN(ci.getTime()) || isNaN(co.getTime())) return '';
  return `${ci.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${co.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

function isParseableDate(s: string): boolean {
  return !!s && !isNaN(new Date(s + 'T00:00:00').getTime());
}

export default function HomeRecentSearches() {
  const { searches, hydrated } = useRecentSearches();

  // Drop entries with malformed/missing dates so legacy/corrupt data doesn't
  // render as "Invalid Date — Invalid Date" cards.
  const renderable = searches.filter(
    (s) => s.dest_id && isParseableDate(s.checkin) && isParseableDate(s.checkout),
  );

  if (!hydrated || renderable.length === 0) return null;

  return (
    <section className="bg-yellow-light pt-16">
      <div className="container">
        <h3 className="font-display text-xl md:text-3xl font-bold text-secondary mb-3">
          Your recent searches
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderable.map((s, idx) => {
            const { checkin, checkout } = bumpPastDates(s.checkin, s.checkout);
            const rooms = parseOccupancyCMS(String(s.rooms), s.adults, s.children);
            const cityLabel = shortDestLabel(s._label || '').split(',')[0] || s._label || 'Search';
            const href = `${PROXY_PREFIX}/hotels/search?${buildQueryString({
              dest_id: s.dest_id,
              checkin,
              checkout,
              rooms: String(s.rooms),
              adults: s.adults,
              children: s.children,
              ...(s.ratecode ? { ratecode: s.ratecode } : {}),
            })}`;
            return (
              <Link
                key={`${s.dest_id}-${s.checkin}-${idx}`}
                href={href}
                prefetch={false}
                className="block bg-white rounded-2xl p-5 hover:shadow-card transition-shadow no-underline"
              >
                <div className="flex items-start gap-4">
                  <i className="fa-regular fa-building text-secondary text-3xl shrink-0 pt-1" aria-hidden="true" />
                  <div className="min-w-0">
                    <h5 className="font-display font-bold text-lg text-secondary leading-tight m-0">
                      {cityLabel}
                    </h5>
                    <p className="mt-1 mb-0 text-sm text-gray-text">
                      {fmtRangeDisplay(checkin, checkout)}, {summarizeOccupancy(rooms)}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
